import { createRoute, type ErrorComponentProps } from "@tanstack/react-router";
import { lazy, type ReactElement, Suspense, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { rootRoute } from "../../root-route";
import { rechargerUneFois } from "../rechargement";
import { BOOSTERS_PATH } from "./boosters-texts";
import {
  CHARGEMENT,
  CHRONIQUES_PATH,
  EDITIONS_PATH,
  INDISPONIBLE,
  RECHARGER,
  SIGNALEMENTS_PATH,
} from "./cartes-texts";

/**
 * Les quatre routes des cartes de la regie : `/regie/cartes/chroniques` (la file de validation),
 * `/regie/cartes/editions`, `/regie/cartes/boosters` et `/regie/cartes/signalements` (les cartes signalees). Les ecrans sont livres a part (ils portent la carte, ses polices et l'apercu) : le paquet
 * d'entree ne les charge qu'a l'ouverture de la page, comme le planning, la diffusion et l'ecran En direct.
 */

class ChargementCartesEchoue extends Error {
  constructor(cause: unknown) {
    super("Chargement de l'ecran des cartes echoue", { cause });
    this.name = "ChargementCartesEchoue";
  }
}

const ChroniquesPage = lazy(() =>
  import("./chroniques").then(
    (module) => ({ default: module.ChroniquesPage }),
    (cause: unknown) => {
      throw new ChargementCartesEchoue(cause);
    },
  ),
);

const EditionsPage = lazy(() =>
  import("./editions").then(
    (module) => ({ default: module.EditionsPage }),
    (cause: unknown) => {
      throw new ChargementCartesEchoue(cause);
    },
  ),
);

const BoostersPage = lazy(() =>
  import("./boosters").then(
    (module) => ({ default: module.BoostersPage }),
    (cause: unknown) => {
      throw new ChargementCartesEchoue(cause);
    },
  ),
);

const SignalementsPage = lazy(() =>
  import("./signalements").then(
    (module) => ({ default: module.SignalementsPage }),
    (cause: unknown) => {
      throw new ChargementCartesEchoue(cause);
    },
  ),
);

const RECHARGER_LA_PAGE = (): void => window.location.reload();

/** Le fichier de l'ecran n'est pas arrive : une phrase, un rechargement tout seul (une fois) et le bouton. */
export function CartesIndisponibles({
  recharger = RECHARGER_LA_PAGE,
}: {
  recharger?: () => void;
}): ReactElement {
  // biome-ignore lint/correctness/useExhaustiveDependencies: une seule fois, au montage de l'ecran d'echec
  useEffect(() => {
    rechargerUneFois(recharger);
  }, []);
  return (
    <section role="alert" className="flex flex-col items-start gap-3">
      <p>{INDISPONIBLE}</p>
      <Button onClick={recharger}>{RECHARGER}</Button>
    </section>
  );
}

function ErreurDesCartes({ error }: ErrorComponentProps): ReactElement {
  if (!(error instanceof ChargementCartesEchoue)) throw error;
  return <CartesIndisponibles />;
}

const attente = (
  <p role="status" className="text-sm text-muted-foreground">
    {CHARGEMENT}
  </p>
);

export const chroniquesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CHRONIQUES_PATH,
  component: () => (
    <Suspense fallback={attente}>
      <ChroniquesPage />
    </Suspense>
  ),
  errorComponent: ErreurDesCartes,
});

export const editionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: EDITIONS_PATH,
  component: () => (
    <Suspense fallback={attente}>
      <EditionsPage />
    </Suspense>
  ),
  errorComponent: ErreurDesCartes,
});

export const boostersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: BOOSTERS_PATH,
  component: () => (
    <Suspense fallback={attente}>
      <BoostersPage />
    </Suspense>
  ),
  errorComponent: ErreurDesCartes,
});

export const signalementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: SIGNALEMENTS_PATH,
  component: () => (
    <Suspense fallback={attente}>
      <SignalementsPage />
    </Suspense>
  ),
  errorComponent: ErreurDesCartes,
});

/** Les routes des cartes, posees dans `appRoutes` (`routes.tsx`). */
export const cartesRoutes = [chroniquesRoute, editionsRoute, boostersRoute, signalementsRoute];
